import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup, FormArray } from '@angular/forms';
import { SubscriptionManagerService } from './services/subscription-manager.service';
import { NarrativeStateService } from './services/narrative-state.service';
import { NarrativeWorkflowService } from './services/narrative-workflow.service';
import { NarrativeErrorHandlerService } from './services/narrative-error-handler.service';
import { SharedModule } from '../shared/shared.module';
import { ServiceIcon } from '../shared/page-header/page-header.component';
import { NarrativeStyleConfigComponent } from './components/narrative-style-config/narrative-style-config.component';
import { NarrativeCsvUploaderComponent } from './components/narrative-csv-uploader/narrative-csv-uploader.component';
import { VideoSettingsCameraOnlyComponent } from '../shared/video-settings-camera-only/video-settings-camera-only.component';
import { CSVUploadResult } from './services/narrative-workflow.service';
import { 
  NarrativeScene, 
  StyleConfiguration, 
  ReferenceImage,
  NarrativeWorkflowState 
} from './models/narrative.model';
import { NarrativeHelper } from './utils/narrative-helper';
import { NARRATIVE_CONSTANTS } from './constants/narrative.constants';
import { NarrativeGoogleService } from './services/narrative-google.service';
import { VideoSettingsCameraOnly } from '../../models/video-settings.model';
import { I18nService } from '../../i18n/i18n.service';

@Component({
  selector: 'app-visual-narrative-maker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SharedModule,
    NarrativeStyleConfigComponent,
    NarrativeCsvUploaderComponent,
    VideoSettingsCameraOnlyComponent
  ],
  templateUrl: './visual-narrative-maker.component.html',
  styleUrls: ['./visual-narrative-maker.component.scss'],
  providers: [SubscriptionManagerService]
})
export class VisualNarrativeMakerComponent implements OnInit, OnDestroy {
  // ヘッダーアイコン設定
  headerIcons: ServiceIcon[] = [
    { src: '/assets/icon_firefly.svg', alt: 'Firefly' },
    { src: '/assets/icon_express.svg', alt: 'Express' },
    { src: '/assets/icon_google_spreadsheet.png', alt: 'Google Sheets' }
  ];

  // Form for backward compatibility
  videoForm!: FormGroup;
  
  // UI state
  activeTab: 'scenes' | 'combined' = 'scenes';
  
  // Observable state
  workflowState$ = this.stateService.workflowState$;
  scenes$ = this.stateService.scenes$;
  isProcessing$ = this.stateService.isProcessing$;
  
  // Current state snapshot for template
  currentState: NarrativeWorkflowState = this.stateService.currentState;
  
  // Regeneration states for UI
  isRegeneratingSceneImage: boolean[] = [];
  isRegeneratingSceneVideo: boolean[] = [];
  
  // Google save state
  isSavingToGoogle = false;
  
  // シーン毎の動画設定展開状態
  sceneVideoSettingsExpanded: boolean[] = [];

  constructor(
    private subscriptionManager: SubscriptionManagerService,
    private stateService: NarrativeStateService,
    private workflowService: NarrativeWorkflowService,
    private errorHandler: NarrativeErrorHandlerService,
    private googleService: NarrativeGoogleService,
    public i18n: I18nService
  ) {}

  ngOnInit(): void {
    this.initializeComponent();
    this.subscribeToStateChanges();
  }

  ngOnDestroy(): void {
    this.subscriptionManager.cleanup();
    this.stateService.resetState();
  }

  private initializeComponent(): void {
    // Initialize form for backward compatibility
    this.videoForm = this.workflowService.createFormGroup();
    
    // Initialize workflow with default scenes
    this.workflowService.initializeWorkflow();
    
    // Initialize regeneration states
    this.initializeRegenerationStates();
  }

  private subscribeToStateChanges(): void {
    this.subscriptionManager.manage(
      this.workflowState$
    ).subscribe(state => {
      this.currentState = state;
      this.updateFormFromState(state);
      this.updateRegenerationStates(state.scenes.length);
    });
  }

  private updateFormFromState(state: NarrativeWorkflowState): void {
    // Update form array to match state (for backward compatibility)
    const scenesArray = this.videoForm.get('scenes') as FormArray;
    while (scenesArray.length !== 0) {
      scenesArray.removeAt(0);
    }
    
    state.scenes.forEach(scene => {
      this.workflowService.addSceneForm(scenesArray);
      const index = scenesArray.length - 1;
      scenesArray.at(index).patchValue({
        prompt: scene.prompt,
        negative_prompt: scene.negativePrompt,
        seed: scene.seed
      });
    });
  }

  private initializeRegenerationStates(): void {
    const sceneCount = this.currentState.scenes.length;
    this.isRegeneratingSceneImage = new Array(sceneCount).fill(false);
    this.isRegeneratingSceneVideo = new Array(sceneCount).fill(false);
    this.sceneVideoSettingsExpanded = new Array(sceneCount).fill(false); // デフォルトで詳細設定を表示
  }

  private updateRegenerationStates(sceneCount: number): void {
    while (this.isRegeneratingSceneImage.length < sceneCount) {
      this.isRegeneratingSceneImage.push(false);
      this.isRegeneratingSceneVideo.push(false);
      this.sceneVideoSettingsExpanded.push(false); // 新しいシーンもデフォルトで詳細設定を表示
    }
    while (this.isRegeneratingSceneImage.length > sceneCount) {
      this.isRegeneratingSceneImage.pop();
      this.isRegeneratingSceneVideo.pop();
      this.sceneVideoSettingsExpanded.pop();
    }
  }

  // Form array getter for template
  get scenes(): FormArray {
    return this.videoForm.get('scenes') as FormArray;
  }

  // Type-safe form group getter
  getFormGroup(index: number): FormGroup {
    const control = this.scenes.at(index);
    return control as FormGroup;
  }

  // Scene management
  addScene(): void {
    // 新しいシーンにデフォルト動画設定を適用
    const defaultVideoSettings = this.currentState.styleConfig.videoSettings;
    
    this.stateService.addScene({
      prompt: '',
      negativePrompt: '',
      seed: undefined,
      videoSettings: defaultVideoSettings ? {
        cameraMotion: defaultVideoSettings.cameraMotion || '',
        promptStyle: '',
        shotAngle: '',
        shotSize: ''
      } : undefined
    });
  }

  removeScene(index: number): void {
    const scenes = this.currentState.scenes;
    if (scenes.length > NARRATIVE_CONSTANTS.UI.MIN_SCENES) {
      const sceneId = scenes[index].id;
      this.stateService.removeScene(sceneId);
    } else {
      this.errorHandler.handleValidationError(
        NARRATIVE_CONSTANTS.ERROR_MESSAGES.MIN_SCENES_REQUIRED
      );
    }
  }

  updateScenePrompt(index: number, prompt: string): void {
    const scene = this.currentState.scenes[index];
    if (scene) {
      this.stateService.updateScene(scene.id, { prompt });
    }
  }

  updateSceneNegativePrompt(index: number, negativePrompt: string): void {
    const scene = this.currentState.scenes[index];
    if (scene) {
      this.stateService.updateScene(scene.id, { negativePrompt });
    }
  }

  updateSceneSeed(index: number, seed: string): void {
    const scene = this.currentState.scenes[index];
    if (scene) {
      this.stateService.updateScene(scene.id, { 
        seed: seed ? parseInt(seed, 10) : undefined 
      });
    }
  }

  // CSV handling
  onScenesLoaded(result: CSVUploadResult): void {
    this.workflowService.loadScenesFromCSV(result);
  }

  onCSVUploadError(error: string): void {
    this.errorHandler.handleValidationError(error);
  }

  // Style configuration
  onStyleConfigChange(config: StyleConfiguration): void {
    // 前の設定と新しい設定を比較して、動画設定が変更されたかチェック
    const previousConfig = this.currentState.styleConfig;
    const previousVideoSettings = previousConfig.videoSettings;
    const newVideoSettings = config.videoSettings;
    
    // 動画設定が変更された場合、個別設定されていないシーンに新しいデフォルト値を適用
    if (this.hasVideoSettingsChanged(previousVideoSettings, newVideoSettings)) {
      this.updateScenesWithNewDefaults(previousVideoSettings, newVideoSettings);
    }
    
    this.stateService.updateStyleConfig(config);
  }

  private hasVideoSettingsChanged(previous: any, current: any): boolean {
    if (!previous && !current) return false;
    if (!previous || !current) return true;
    
    return previous.cameraMotion !== current.cameraMotion;
  }

  private updateScenesWithNewDefaults(previousDefaults: any, newDefaults: any): void {
    if (!newDefaults) return;
    
    this.currentState.scenes.forEach(scene => {
      if (!scene.videoSettings) {
        // 個別設定がない場合、新しいデフォルト値を設定
        this.stateService.updateScene(scene.id, {
          videoSettings: {
            cameraMotion: newDefaults.cameraMotion || '',
            promptStyle: '',
            shotAngle: '',
            shotSize: ''
          }
        });
      } else {
        // 個別設定がある場合、前のデフォルト値と同じ項目のみ新しいデフォルト値に更新
        const updatedVideoSettings = { ...scene.videoSettings };
        let hasChanges = false;

        if (previousDefaults?.cameraMotion === scene.videoSettings.cameraMotion) {
          updatedVideoSettings.cameraMotion = newDefaults.cameraMotion || '';
          hasChanges = true;
        }

        if (hasChanges) {
          this.stateService.updateScene(scene.id, { videoSettings: updatedVideoSettings });
        }
      }
    });
  }

  onStyleImageUploaded(image: ReferenceImage): void {
    this.subscriptionManager.manage(
      this.workflowService.uploadStyleImage(image)
    ).subscribe();
  }

  onStyleImageRemoved(): void {
    this.workflowService.removeStyleImage();
  }

  onValidationError(error: string): void {
    this.errorHandler.handleValidationError(error);
  }

  // Image generation
  generateSceneImages(): void {
    this.subscriptionManager.manage(
      this.workflowService.generateAllSceneImages()
    ).subscribe();
  }

  regenerateSceneImage(index: number): void {
    const scene = this.currentState.scenes[index];
    if (!scene) return;

    this.isRegeneratingSceneImage[index] = true;
    
    // 既存の画像をクリア
    this.stateService.updateScene(scene.id, { 
      images: [],
      selectedImageIndex: 0
    });
    
    this.subscriptionManager.manage(
      this.workflowService.regenerateSceneImage(scene.id)
    ).subscribe({
      next: () => {
        this.isRegeneratingSceneImage[index] = false;
      },
      error: () => {
        this.isRegeneratingSceneImage[index] = false;
      }
    });
  }

  // Video generation
  generateVideoFromImages(): void {
    this.subscriptionManager.manage(
      this.workflowService.generateVideosFromScenes()
    ).subscribe();
  }

  regenerateSceneVideo(index: number): void {
    const scene = this.currentState.scenes[index];
    if (!scene) return;

    this.isRegeneratingSceneVideo[index] = true;
    
    this.subscriptionManager.manage(
      this.workflowService.regenerateSceneVideo(scene.id)
    ).subscribe({
      complete: () => {
        this.isRegeneratingSceneVideo[index] = false;
      },
      error: () => {
        this.isRegeneratingSceneVideo[index] = false;
      }
    });
  }

  regenerateCombinedVideo(): void {
    this.subscriptionManager.manage(
      this.workflowService.combineVideos()
    ).subscribe();
  }

  // Video editing
  editCombinedVideo(): void {
    this.subscriptionManager.manage(
      this.workflowService.editCombinedVideo()
    ).subscribe();
  }

  // 動画設定変更
  onSceneVideoSettingsChanged(sceneIndex: number, settings: VideoSettingsCameraOnly): void {
    const scene = this.currentState.scenes[sceneIndex];
    if (scene) {
      // カメラモーションのみの設定をシーンに保存
      this.stateService.updateScene(scene.id, { 
        videoSettings: {
          cameraMotion: settings.cameraMotion,
          promptStyle: '',
          shotAngle: '',
          shotSize: ''
        }
      });
    }
  }

  toggleSceneVideoSettings(sceneIndex: number): void {
    this.sceneVideoSettingsExpanded[sceneIndex] = !this.sceneVideoSettingsExpanded[sceneIndex];
  }

  // Scene selection
  onImageSelected(sceneIndex: number, imageIndex: number): void {
    const scene = this.currentState.scenes[sceneIndex];
    if (scene) {
      this.stateService.updateSelectedImageIndex(scene.id, imageIndex);
    }
  }

  // Helper methods
  getSelectedImageUrl(sceneIndex: number): string | null {
    const scene = this.currentState.scenes[sceneIndex];
    if (!scene || scene.images.length === 0) return null;
    
    const selectedIndex = scene.selectedImageIndex || 0;
    return scene.images[selectedIndex]?.url || null;
  }

  areAllSceneImagesGenerated(): boolean {
    return this.stateService.areAllScenesGenerated();
  }

  hasMultipleSceneVideos(): boolean {
    return this.stateService.hasMultipleSceneVideos();
  }

  hasSelectedImages(): boolean {
    return this.currentState.scenes.some(scene => 
      scene.images && 
      scene.images.length > 0 && 
      scene.selectedImageIndex !== undefined &&
      scene.selectedImageIndex >= 0
    );
  }

  // Google save functionality
  saveSelectedImagesToGoogle(): void {
    if (this.isSavingToGoogle) return;
    
    this.isSavingToGoogle = true;
    
    this.subscriptionManager.manage(
      this.googleService.saveSelectedImages(
        this.currentState.scenes,
        this.currentState.styleConfig
      )
    ).subscribe({
      next: (result) => {
        this.isSavingToGoogle = false;
        
        if (result.success) {
          if (result.savedCount === 1) {
            this.errorHandler.showSuccess('画像がGoogle Driveに保存されました');
          } else {
            this.errorHandler.showSuccess(`${result.savedCount}枚の画像がGoogle Driveに保存されました`);
          }
        } else {
          this.errorHandler.handleError(new Error('画像の保存に失敗しました'), { type: 'validation' });
        }
        
        if (result.errors.length > 0) {
          console.error('保存エラー:', result.errors);
        }
      },
      error: (error) => {
        this.isSavingToGoogle = false;
        this.errorHandler.handleError(error, { type: 'validation' });
        console.error('Google save error:', error);
      }
    });
  }

  formatFileSize = NarrativeHelper.formatFileSize;

  // Track by functions for performance
  trackBySceneId(_index: number, scene: NarrativeScene): string {
    return scene.id;
  }

  trackByIndex(index: number): number {
    return index;
  }
}